import { FrontCameraProfile } from '@editor/interfaces/camera'
import { PointArray } from '@editor/interfaces/interfaces'
import { Box3, OrthographicCamera, PerspectiveCamera, Vector3 } from 'three'

import { findCenter } from './Points'
import { ArcballControls } from './threejs'

export const computeOrthoMinZoomToFit = (
  box: Box3,
  camera: OrthographicCamera,
  padding = 1.0, // 1.0 = no padding, >1 adds space
): number => {
  const size = box.getSize(new Vector3())

  // Get the desired size of the view
  const viewWidth = size.x * padding
  const viewHeight = size.y * padding

  const fullViewWidth = camera.right - camera.left
  const fullViewHeight = camera.top - camera.bottom

  // Calculate our scale based on camera's frustrum
  const zoomForWidth = fullViewWidth / viewWidth
  const zoomForHeight = fullViewHeight / viewHeight

  // Use the smaller zoom (which shows the larger area)
  return Math.min(zoomForWidth, zoomForHeight)
}

/**
 * Generate camera profile.
 *
 * @param camera - The camera to generate the profile from.
 * @param arcballControls - The ArcballControls instance used to extract gizmo and target information.
 * @returns The generated camera profile or null if the target point cannot be determined.
 */
export const getCameraProfile = (
  camera: OrthographicCamera | PerspectiveCamera,
  arcballControls: ArcballControls,
): FrontCameraProfile | null => {
  const cameraMatrix = camera.matrix.toArray()
  const gizmoMatrix = arcballControls.gizmos.matrix.toArray()

  return {
    state: {
      arcballState: {
        cameraFar: camera.far,
        cameraFov: 'fov' in camera ? camera.fov : -1,
        cameraMatrix: { elements: cameraMatrix },
        cameraNear: camera.near,
        cameraUp: {
          x: cameraMatrix[4],
          y: cameraMatrix[5],
          z: cameraMatrix[6],
        },
        cameraZoom: camera.zoom,
        gizmoMatrix: {
          elements: gizmoMatrix,
        },
      },
    },
    target: arcballControls.gizmos.position.toArray(),
    positions: {
      front: camera.position.toArray(),
    },
  }
}

/**
 * Calculates the required camera distance so that all given points
 * fit within the camera's view, with some padding.
 *
 * Assumptions:
 * - Points lie on a plane perpendicular to camera direction
 * - Camera looks straight at the plane center
 * - FOV is vertical FOV in degrees
 *
 * @param points Array of 3D coordinates
 * @param fovDeg Vertical field of view in degrees
 * @param aspect Aspect ratio (width / height)
 * @returns Required camera distance. Returns 1 if points array is empty or target cannot be determined.
 */
export function fitCameraDistance(
  points: (Vector3 | PointArray)[],
  normal: Vector3,
  cameraUp: Vector3,
  fovDeg: number,
  aspect: number,
  padding = 1.0,
): number {
  if (points.length === 0) return 1

  const pointsv3 = points.map((p) => (p instanceof Vector3 ? p : new Vector3(...p)))
  const target = findCenter(pointsv3)
  if (!target) return 1

  // Calculate required camera distance to fit the whole plane in view, with some padding
  // Transform 3D points to camera-aligned coordinate system for accurate distance calculation
  const forward = normal.clone().normalize()
  const right = new Vector3().crossVectors(cameraUp, forward).normalize()
  const up = new Vector3().crossVectors(forward, right).normalize()

  // Project points to 2D in camera's viewing plane
  const points2D = pointsv3.map((pos) => {
    const p = pos.clone().sub(target)
    return new Vector3(p.dot(right), p.dot(up), 0)
  })

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  points2D.forEach((p) => {
    minX = Math.min(minX, p.x)
    maxX = Math.max(maxX, p.x)
    minY = Math.min(minY, p.y)
    maxY = Math.max(maxY, p.y)
  })

  const width = maxX - minX
  const height = maxY - minY

  const fovRad = (fovDeg * Math.PI) / 180
  const tanHalfFov = Math.tan(fovRad / 2)

  const distanceHeight = height / (2 * tanHalfFov)
  const distanceWidth = width / (2 * tanHalfFov * aspect)

  return Math.max(distanceHeight, distanceWidth) * padding
}
